import React, { useState } from 'react';
import { View } from 'react-native';
import { translate } from '../../../services/translate';
import moment from 'moment';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableContainer from '../../../components/DataTableContainer';
import MyDataTable from '../../../components/MyDataTable';
import { Pagination } from '../../../components/Pagination';
import { Tabs } from '../../../components/Tabs';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import { DocumentsParamList } from '../../../typings/Params';
import { documentService } from '../../../services/central-api/document';
import { useQuery } from '@tanstack/react-query';
import CustomButton from '../../../components/CustomButton';
import DropdownButton from '../../../components/DropdownButton';
import useFilters from '../../../components/Filter/hooks/useFilters';
import MyAppText from '../../../components/MyAppText';
import TermsVersionsCitizenList from './TermsVersionsList';
import { downloadTermsVersion } from '../DownloadTermsVersion';
import DataTableHeader from '../../../components/DataTableHeader';
import BasicModal from '../../../components/BasicModal';
import { TranslateRich } from '../../../components/TranslateRich';

export default function TermsAndPrivacyCitizenApp() {
    const route = useRoute<RouteProp<DocumentsParamList, 'TermsAndPrivacyCitizenApp'>>();
    const navigation = useNavigation<NavigationProp<DocumentsParamList>>();

    const [filters, setFilters] = useFilters<DocumentsParamList['TermsAndPrivacyCitizenApp']>({
        page: Math.max(route.params.page - 1, 0),
        limit: Number(route.params.limit) || 25,
    });

    const [duplicateTarget, setDuplicateTarget] = useState<{ id: number; version: number; label: string; }>();
    const [duplicateVisible, setDuplicateVisible] = useState(false);

    const isFocused = useIsFocused();
    const { data, isLoading } = useQuery({
        queryKey: ['getTermsOfUseAndPrivacy', 'citizen_app', filters],
        queryFn: () => documentService.getTermsOfUseAndPrivacy({ ...filters, application: 'citizen_app' }),
        enabled: isFocused,
    });

    return <Container>
        <Header
            title={translate('TermsAndPrivacyCitizenApp')}
            actions={<CustomButton text={translate('register')} onPress={() => {
                navigation.navigate('TermsAndPrivacyCitizenForm');
            }} />}
        />
        <Card>
            <Tabs tabs={[{
                key: 'registered',
                label: translate('registeredTermsAndPrivacy'),
            }, {
                key: 'scheduled',
                label: translate('scheduledDocuments'),
            }, {
                key: 'versions',
                label: translate('previousVersions'),
            }]}>
                {({ selectedTab }) => {
                    if (selectedTab == 'registered') {
                        return (
                            <View style={{ flex: 1 }}>
                                <DataTableHeader
                                    zIndex={2}
                                    title={translate('currentTermsAndPrivacy')}
                                />
                                <DataTableContainer
                                    dataTable={<MyDataTable
                                        columns={[
                                            {
                                                name: translate('documentType'),
                                                selector: row => translate(row.documentType),
                                                wrap: true,
                                            },
                                            {
                                                name: translate('effectiveDate'),
                                                selector: row => row.versions[0]?.effectiveDate
                                                    ? moment(row.versions[0].effectiveDate).format('DD/MM/YYYY')
                                                    : '-',
                                                wrap: true,
                                            },
                                            {
                                                name: translate('documentVersion'),
                                                selector: row => row.versions[0]?.version ?? '-',
                                            },
                                            {
                                                name: translate('language'),
                                                selector: row => row.language.toUpperCase(),
                                                wrap: true,
                                            },
                                            {
                                                cell: row => {
                                                    const version = row.versions[0].version;

                                                    return (
                                                        <DropdownButton
                                                            actions={[
                                                                {
                                                                    content: translate('viewDocument'),
                                                                    onPress: () => {
                                                                        navigation.navigate('TermsAndPrivacyCitizenForm', { id: row.id, version, mode: 'view' });
                                                                    }
                                                                },
                                                                {
                                                                    content: translate('duplicateTermsTitle'),
                                                                    onPress: () => {
                                                                        setDuplicateTarget({
                                                                            id: row.id,
                                                                            version,
                                                                            label: `${translate(row.documentType)} (${version}) - ${row.language.toUpperCase()}`,
                                                                        });
                                                                        setDuplicateVisible(true);
                                                                    }
                                                                },
                                                                {
                                                                    content: translate('downloadDocument'),
                                                                    onPress: () => downloadTermsVersion(row.id, version),
                                                                },
                                                            ]}
                                                        />
                                                    );
                                                },
                                                wrap: true,
                                                button: true,
                                            }
                                        ]}
                                        data={data?.rows ?? []}
                                        progressPending={isLoading}
                                    />}
                                    pagination={<Pagination
                                        totalItems={data?.count ?? 0}
                                        currentPage={filters.page}
                                        pageSize={filters.limit}
                                        setPageSize={pageSize => {
                                            setFilters(old => ({
                                                ...old,
                                                limit: pageSize,
                                                page: 0,
                                            }));

                                            navigation.setParams({
                                                ...route.params,
                                                limit: pageSize,
                                                page: 1,
                                            });
                                        }}
                                        setPage={page => {
                                            setFilters(old => ({
                                                ...old,
                                                page
                                            }));

                                            navigation.setParams({
                                                ...route.params,
                                                page: page + 1
                                            });
                                        }}
                                    />}
                                />
                            </View>
                        );
                    }

                    if (selectedTab == 'scheduled') {
                        return (
                            <View style={{ flex: 1 }}>
                                <TermsVersionsCitizenList status='scheduled' />
                            </View>
                        );
                    }

                    if (selectedTab == 'versions') {
                        return (
                            <View style={{ flex: 1 }}>
                                <TermsVersionsCitizenList status='previous' />
                            </View>
                        );
                    }
                }}
            </Tabs>
        </Card>

        <BasicModal
            isModalVisible={duplicateVisible}
            setModalVisible={setDuplicateVisible}
            width='small'
            mobileHeight='fit'
            title={translate('duplicateTermsTitle')}
            primaryButton={{
                text: translate('duplicate'),
                onClick: () => {
                    if (duplicateTarget) {
                        navigation.navigate('TermsAndPrivacyCitizenForm', {
                            id: duplicateTarget.id,
                            version: duplicateTarget.version,
                            mode: 'duplicate',
                        });
                    }
                    setDuplicateTarget(undefined);
                },
            }}
        >
            <MyAppText>
                <TranslateRich
                    i18nKey='duplicateTermsText'
                    values={{ name: <MyAppText style={{ fontWeight: 'bold' }}>{duplicateTarget?.label ?? ''}</MyAppText> }}
                />
            </MyAppText>
            <MyAppText>{translate('duplicateTermsQuestion')}</MyAppText>
        </BasicModal>
    </Container>;
}
